import React, {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { InputText, TInputTextProps } from './InputText';

type TInputNumberProps = {
  allowDecimal?: boolean;
  nullValue?: number | null;
  onChange?: (v: number | null) => void;
  onScroll?: React.UIEventHandler<HTMLInputElement>;
  postfix?: string;
  value?: number;
} & Omit<TInputTextProps, 'value' | 'onChange' | 'onScroll'>;

const INTEGER_REGEXP = /^\d+$/;
const DECIMAL_REGEXP = /^\d*(?:[.,]\d*)?$/;

function transformValue(value?: number | string) {
  return value === 0 ? '' : `${value ?? ''}`;
}

export const InputNumber = forwardRef<HTMLInputElement, TInputNumberProps>(
  function InputNumber(props: TInputNumberProps, ref) {
    const {
      allowDecimal = false,
      maxLength,
      nullValue = 0,
      onChange,
      onScroll,
      placeholder,
      postfix = '',
      value,
      ...rest
    } = props;
    const [localValue, setLocalValue] = useState(transformValue(value));

    const handleChange = useCallback(
      (value: string, event?: ChangeEvent<HTMLInputElement>) => {
        const cleanedValue = value.replace(postfix, '');
        const normalizedValue = allowDecimal
          ? cleanedValue.replace(',', '.')
          : cleanedValue;

        onChange?.(
          normalizedValue === '' ? nullValue : Number(normalizedValue),
        );
        setLocalValue(normalizedValue);
        if (postfix && event?.target)
          requestAnimationFrame(() => {
            const { target } = event;
            if (
              target.selectionStart &&
              target.selectionStart > target.value.length - postfix.length
            )
              target.setSelectionRange(
                target.value.length - postfix.length,
                target.value.length - postfix.length,
              );
          });
      },
      [allowDecimal, onChange, nullValue, postfix],
    );

    const handleFocus = useCallback(
      (e: FocusEvent<HTMLInputElement>) => {
        if (e?.target?.value === '0' + postfix) e?.target?.select();
      },
      [postfix],
    );

    useEffect(() => {
      const extValue = transformValue(value);
      if (localValue !== extValue) {
        const timeoutId = setTimeout(() => setLocalValue(extValue), 0);
        return () => clearTimeout(timeoutId);
      }
      return undefined;
    }, [value, localValue]);

    return (
      <InputText
        allowedPattern={allowDecimal ? DECIMAL_REGEXP : INTEGER_REGEXP}
        inputMode={allowDecimal ? 'decimal' : 'numeric'}
        maxLength={maxLength ? maxLength + (postfix?.length || 0) : undefined}
        onChange={handleChange}
        onFocus={handleFocus}
        onScroll={onScroll}
        pattern={allowDecimal ? '[0-9]*[.,]?[0-9]*' : '[0-9]*'}
        placeholder={placeholder}
        value={localValue ? localValue + postfix : ''}
        ref={ref}
        // Filter only input-compatible props
        {...Object.fromEntries(
          Object.entries(rest).filter(([key, value]) => {
            // Exclude textarea-specific props
            if (key === 'rows' || key === 'cols' || key === 'wrap')
              return false;

            // For event handlers, allow only those that work with input
            if (key.startsWith('on') && typeof value === 'function') {
              // Allow basic event handlers
              const allowedHandlers = [
                'onCopy',
                'onCut',
                'onPaste',
                'onCompositionStart',
                'onCompositionEnd',
                'onCompositionUpdate',
                'onSelect',
                'onBeforeInput',
                'onInput',
                'onFocus',
                'onBlur',
                'onClick',
                'onDoubleClick',
                'onMouseDown',
                'onMouseUp',
                'onMouseEnter',
                'onMouseLeave',
                'onMouseMove',
                'onKeyDown',
                'onKeyUp',
                'onKeyPress',
                'onWheel',
                'onDrag',
                'onDragStart',
                'onDragEnd',
                'onDragOver',
                'onDrop',
                'onTouchStart',
                'onTouchEnd',
                'onTouchMove',
                'onContextMenu',
              ];
              return allowedHandlers.includes(key);
            }

            return true;
          }),
        )}
      />
    );
  },
);
